import type { DayId, DayKind } from './calendar.ts'
import { addDays, diffDays, screenBudgetFor, weekStart } from './calendar.ts'
import type {
  Actor,
  Adaptations,
  DomainEvent,
  GameId,
  Moment,
  PracticeItem,
  RewardLadder,
  Rung,
  ScreenLimits,
  Step,
  TaskKind,
} from './events.ts'
import { mayActorEmit } from './events.ts'
import { PREGUNTAS_POR_SESION } from './rules/mates.ts'
import { adultEvent, childEvents, type ChildSetup } from './seed.ts'
import type { CommandId, MemberId, PhotoId, PurchaseId, RewardId, StepId, TaskId } from './ids.ts'
import {
  adultsOf,
  dayOf,
  memberDayOf,
  routinesOf,
  taskDayOf,
  type FamilyState,
  type Reward,
} from './state.ts'

/**
 * Commands: the only way anything enters the log.
 *
 * Every handler is a pure function of (state, command, context) and returns
 * either events or a refusal. Nothing here reads the clock or throws dice: both
 * arrive in the context and are written into the events, so replaying the log
 * always produces the same state.
 *
 * Refusals carry a code, never a sentence. The Spanish the family reads lives in
 * `ui/strings/es.ts`, where a test checks the language.
 */

export type ErrorCode =
  | 'not-configured'
  | 'unknown-member'
  | 'unknown-task'
  | 'unknown-reward'
  | 'unknown-purchase'
  | 'not-allowed'
  | 'steps-missing'
  | 'photo-required'
  | 'already-done'
  | 'not-marked'
  | 'routine-cannot-pay'
  | 'chest-not-ready'
  | 'chest-already-open'
  | 'no-screen-today'
  | 'not-enough-coins'
  | 'daily-cap-reached'
  | 'reward-limit-reached'
  | 'reward-not-for-member'
  | 'already-given'
  | 'invalid'

export interface DomainError {
  readonly code: ErrorCode
  /** Machine-readable context for the message, never a user-facing sentence. */
  readonly detail?: Record<string, string | number>
}

export type CommandResult =
  | { readonly ok: true; readonly events: readonly DomainEvent[] }
  | { readonly ok: false; readonly error: DomainError }

export interface CommandContext {
  readonly now: Date
  readonly day: DayId
  readonly actor: Actor
  /** Fresh identifiers, supplied so the domain stays deterministic. */
  readonly newId: () => string
  /** A draw in [0, 1), recorded in the event when one is needed. */
  readonly draw: () => number
}

const fail = (code: ErrorCode, detail?: DomainError['detail']): CommandResult => ({
  ok: false,
  error: detail === undefined ? { code } : { code, detail },
})

const done = (...events: DomainEvent[]): CommandResult => ({ ok: true, events })

function base(ctx: CommandContext): { at: string; day: DayId; actor: Actor; v: 1 } {
  return { at: ctx.now.toISOString(), day: ctx.day, actor: ctx.actor, v: 1 }
}

/** Actors can only emit what the vocabulary allows them to. */
function forbidden(ctx: CommandContext, type: DomainEvent['type']): boolean {
  return !mayActorEmit(ctx.actor, type)
}

// --- Configuration -----------------------------------------------------------

export interface DefineTaskCommand {
  readonly taskId: TaskId
  readonly memberId: MemberId
  readonly kind: TaskKind
  readonly moment?: Moment
  readonly name: string
  readonly emoji?: string
  readonly steps: readonly Step[]
  readonly difficulty: number
  readonly rewards: RewardLadder
  readonly promotionThreshold: number
  readonly requiresPhoto: boolean
  readonly initialRung?: Rung
  /** Qué juego la da por hecha, si es una misión de juego. */
  readonly game?: GameId
}

export function defineTask(
  state: FamilyState,
  command: DefineTaskCommand,
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskDefined')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')

  // The rule the whole system rests on: a routine is contribution, not work.
  // It earns experience and keeps the day moving; it never earns coins. Paying
  // for it turns "what we all do because we live here" into a transaction.
  if (command.kind === 'routine') {
    const paid = ([1, 2, 3, 4] as const).filter((rung) => command.rewards[rung].coins > 0)
    if (paid.length > 0) return fail('routine-cannot-pay', { rungs: paid.join(',') })
  }
  if (command.kind === 'routine' && command.moment === undefined) {
    return fail('invalid', { field: 'moment' })
  }

  return done({
    ...base(ctx),
    type: 'TaskDefined',
    payload: {
      taskId: command.taskId,
      memberId: command.memberId,
      kind: command.kind,
      ...(command.moment !== undefined ? { moment: command.moment } : {}),
      name: command.name,
      ...(command.emoji !== undefined ? { emoji: command.emoji } : {}),
      steps: [...command.steps],
      difficulty: command.difficulty,
      rewards: command.rewards,
      promotionThreshold: command.promotionThreshold,
      requiresPhoto: command.requiresPhoto,
      ...(command.initialRung !== undefined ? { initialRung: command.initialRung } : {}),
      ...(command.game !== undefined ? { game: command.game } : {}),
    },
  })
}

/**
 * A new thing to buy. Until now the shop only ever came from the seed, which
 * meant the catalogue was fixed on day one — exactly the opposite of what the
 * weekly meeting is for.
 */
export function defineReward(
  state: FamilyState,
  command: {
    rewardId: RewardId
    name: string
    price: number
    kind: Reward['kind']
    memberIds: readonly MemberId[]
    notes?: string
    limit?: { per: 'day' | 'week'; max: number }
  },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'RewardDefined')) return fail('not-allowed')
  if (command.name.trim().length === 0) return fail('invalid', { field: 'name' })
  if (command.price < 0) return fail('invalid', { field: 'price' })
  if (command.memberIds.length === 0) return fail('invalid', { field: 'memberIds' })
  for (const id of command.memberIds) {
    if (!state.members.has(id)) return fail('unknown-member')
  }

  return done({
    ...base(ctx),
    type: 'RewardDefined',
    payload: {
      rewardId: command.rewardId,
      name: command.name.trim(),
      price: command.price,
      kind: command.kind,
      memberIds: [...command.memberIds],
      ...(command.notes !== undefined ? { notes: command.notes } : {}),
      ...(command.limit !== undefined ? { limit: command.limit } : {}),
    },
  })
}

/** Takes something out of the shop without pretending it was never there. */
export function archiveReward(
  state: FamilyState,
  command: { rewardId: RewardId; reason: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'RewardArchived')) return fail('not-allowed')
  if (!state.rewards.has(command.rewardId)) return fail('unknown-reward')
  return done({
    ...base(ctx),
    type: 'RewardArchived',
    payload: { rewardId: command.rewardId, reason: command.reason },
  })
}

/**
 * A price move. It is scheduled rather than applied, because a price that
 * changes under a child who was saving for it is the fastest way to teach that
 * saving is pointless. `effectiveFrom` defaults to the next meeting day.
 */
export function repriceReward(
  state: FamilyState,
  command: { rewardId: RewardId; newPrice: number; effectiveFrom: DayId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'PriceChangeScheduled')) return fail('not-allowed')
  const reward = state.rewards.get(command.rewardId)
  if (!reward?.active) return fail('unknown-reward')
  if (command.newPrice < 0) return fail('invalid', { field: 'newPrice' })

  return done({
    ...base(ctx),
    type: 'PriceChangeScheduled',
    payload: {
      rewardId: command.rewardId,
      newPrice: command.newPrice,
      effectiveFrom: command.effectiveFrom,
    },
  })
}

/**
 * Cambia lo que paga una tarea.
 *
 * No existía: el precio se ponía al crearla y después no había manera de
 * tocarlo, que es lo contrario de para lo que existe una reunión semanal.
 *
 * Toma efecto ya, como el nombre y los pasos, y por el mismo motivo: no es un
 * cambio de reglas, es una corrección de lo que se acordó. El niño que necesita
 * avisos lo ve, porque la proyección marca la tarea como cambiada hoy.
 */
export function retuneTask(
  state: FamilyState,
  command: { taskId: TaskId; rewards: RewardLadder },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskRewardsChanged')) return fail('not-allowed')

  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')

  // La misma regla que al crearla, comprobada en el mismo sitio: una rutina es
  // contribución y no trabajo pagado, venga el cambio de donde venga.
  if (task.kind === 'routine') {
    const paid = ([1, 2, 3, 4] as const).filter((rung) => command.rewards[rung].coins > 0)
    if (paid.length > 0) return fail('routine-cannot-pay', { rungs: paid.join(',') })
  }

  return done({
    ...base(ctx),
    type: 'TaskRewardsChanged',
    payload: { taskId: task.id, rewards: command.rewards },
  })
}

/**
 * A qué horas se le recuerda a un niño lo que toca.
 *
 * Es configuración de la casa y por eso va al registro: de aquí sale el aviso
 * que se ve en la pantalla, que funciona sin red. La copia que va al servidor
 * para el aviso empujado se manda aparte y es prescindible.
 */
export function setReminders(
  state: FamilyState,
  command: { memberId: MemberId; avisos: readonly { hora: string; previo: boolean }[] },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'RemindersSet')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')

  const limpios = command.avisos
    .filter((aviso) => /^([01]\d|2[0-3]):[0-5]\d$/.test(aviso.hora))
    .slice(0, 6)
    .map((aviso) => ({ hora: aviso.hora, previo: aviso.previo }))

  return done({
    ...base(ctx),
    type: 'RemindersSet',
    payload: { memberId: command.memberId, avisos: limpios },
  })
}

/**
 * El dibujo, el color y el nombre de las monedas de un niño.
 *
 * Es la única forma que tiene esta app de meter el interés fuerte de un niño
 * dentro del juego, que en TEA es el motivador más potente que existe, sin
 * traer contenido que no es de esta casa. Lo que se pone son palabras y emojis
 * que elige la familia.
 */
export function updateLook(
  state: FamilyState,
  command: { memberId: MemberId; accent: string; emoji?: string; coinWord?: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberLookChanged')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')
  if (!/^#[0-9a-f]{6}$/i.test(command.accent)) return fail('invalid', { field: 'accent' })

  const coinWord = command.coinWord?.trim()
  return done({
    ...base(ctx),
    type: 'MemberLookChanged',
    payload: {
      memberId: command.memberId,
      accent: command.accent,
      ...(command.emoji !== undefined && command.emoji.length > 0 ? { emoji: command.emoji } : {}),
      ...(coinWord !== undefined && coinWord.length > 0 ? { coinWord } : {}),
    },
  })
}

/** A clearer name is a clearer agreement. */
export function renameTask(
  state: FamilyState,
  command: { taskId: TaskId; name: string; emoji?: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskRenamed')) return fail('not-allowed')
  if (!state.tasks.has(command.taskId)) return fail('unknown-task')
  if (command.name.trim().length === 0) return fail('invalid', { field: 'name' })
  return done({
    ...base(ctx),
    type: 'TaskRenamed',
    payload: {
      taskId: command.taskId,
      name: command.name.trim(),
      ...(command.emoji !== undefined && command.emoji !== '' ? { emoji: command.emoji } : {}),
    },
  })
}

/**
 * Rewriting the steps. This is the lever that matters most: a child who stalls
 * at the same step every day is usually looking at a step that is really three,
 * and splitting it in two is the whole fix.
 */
export function updateSteps(
  state: FamilyState,
  command: { taskId: TaskId; steps: readonly Step[] },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskStepsUpdated')) return fail('not-allowed')
  if (!state.tasks.has(command.taskId)) return fail('unknown-task')
  if (command.steps.length > 8) return fail('invalid', { field: 'steps' })
  if (command.steps.some((s) => s.text.trim().length === 0))
    return fail('invalid', { field: 'steps' })
  const ids = new Set(command.steps.map((s) => s.id))
  if (ids.size !== command.steps.length) return fail('invalid', { field: 'steps' })
  return done({
    ...base(ctx),
    type: 'TaskStepsUpdated',
    payload: {
      taskId: command.taskId,
      steps: command.steps.map((s) => ({ ...s, text: s.text.trim() })),
    },
  })
}

/**
 * Un hijo más, después de haber creado la casa.
 *
 * Trae lo mismo que traería al crearla —sus rutinas y sus misiones, de la misma
 * semilla— porque un hijo dado de alta a mano y sin nada que hacer deja al padre
 * escribiéndole el día entero tarea por tarea.
 *
 * Y entra en los premios que ya hay. El catálogo se definió con la lista de hijos
 * de aquel día, así que sin esto el nuevo abriría la tienda y la encontraría
 * vacía: no es lo que nadie espera al añadir a alguien de la familia.
 */
export function addChild(
  state: FamilyState,
  command: ChildSetup,
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberAdded')) return fail('not-allowed')
  if (state.members.has(command.id)) return fail('already-done')
  if (command.name.trim().length === 0) return fail('invalid', { field: 'name' })

  const head = base(ctx)
  const events = [...childEvents({ ...command, name: command.name.trim() }, head)]

  for (const reward of state.rewards.values()) {
    if (!reward.active) continue
    if (reward.memberIds.includes(command.id)) continue
    events.push({
      ...head,
      type: 'RewardMembersChanged',
      payload: { rewardId: reward.id, memberIds: [...reward.memberIds, command.id] },
    })
  }

  return done(...events)
}

/**
 * Un adulto más.
 *
 * No trae tareas. Lo que trae es poder aparecer en «¿quién eres?» al abrir el modo
 * padres, y que el registro diga quién validó cada cosa en vez de atribuírselo
 * todo a la primera persona que se dio de alta.
 */
export function addAdult(
  state: FamilyState,
  command: { memberId: MemberId; name: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberAdded')) return fail('not-allowed')
  if (state.members.has(command.memberId)) return fail('already-done')
  if (command.name.trim().length === 0) return fail('invalid', { field: 'name' })
  return done(adultEvent({ id: command.memberId, name: command.name.trim() }, base(ctx)))
}

/**
 * Quitar a alguien de las listas.
 *
 * Hace falta sobre todo para deshacer un desaguisado: importar una copia en una
 * tablet que ya tenía casa deja miembros duplicados, y hasta ahora no había
 * salida ninguna. También sirve para alguien que ya no vive aquí.
 *
 * No borra nada. Su historia se queda en el registro porque ocurrió, y sus
 * monedas y su experiencia siguen ahí si vuelve.
 */
export function archiveMember(
  state: FamilyState,
  command: { memberId: MemberId; reason: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberArchived')) return fail('not-allowed')
  const member = state.members.get(command.memberId)
  if (!member) return fail('unknown-member')
  if (member.archived) return fail('already-done')

  // Sin ningún adulto a la vista no se puede abrir el modo padres, y entonces no
  // habría forma de deshacer esto desde dentro de la app.
  if (member.role === 'parent' && adultsOf(state).length <= 1) {
    return fail('invalid', { field: 'role' })
  }

  return done({
    ...base(ctx),
    type: 'MemberArchived',
    payload: { memberId: command.memberId, reason: command.reason },
  })
}

export function restoreMember(
  state: FamilyState,
  command: { memberId: MemberId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberRestored')) return fail('not-allowed')
  const member = state.members.get(command.memberId)
  if (!member) return fail('unknown-member')
  if (!member.archived) return fail('already-done')
  return done({ ...base(ctx), type: 'MemberRestored', payload: { memberId: command.memberId } })
}

/** Out of the way, not deleted: the history of what was done stays readable. */
export function archiveTask(
  state: FamilyState,
  command: { taskId: TaskId; reason: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskArchived')) return fail('not-allowed')
  const task = state.tasks.get(command.taskId)
  if (!task) return fail('unknown-task')
  if (task.archived) return fail('already-done')
  return done({
    ...base(ctx),
    type: 'TaskArchived',
    payload: { taskId: command.taskId, reason: command.reason },
  })
}

export function restoreTask(
  state: FamilyState,
  command: { taskId: TaskId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskRestored')) return fail('not-allowed')
  const task = state.tasks.get(command.taskId)
  if (!task) return fail('unknown-task')
  if (!task.archived) return fail('already-done')
  return done({ ...base(ctx), type: 'TaskRestored', payload: { taskId: command.taskId } })
}

export function updateAdaptations(
  state: FamilyState,
  command: { memberId: MemberId; adaptations: Adaptations },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'MemberAdaptationsUpdated')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')
  return done({
    ...base(ctx),
    type: 'MemberAdaptationsUpdated',
    payload: { memberId: command.memberId, adaptations: command.adaptations },
  })
}

/**
 * The photograph of what "done" looks like.
 *
 * This is the answer to "but I did do my job". A child who has understood the
 * task their own way is not arguing in bad faith: the agreement was never as
 * explicit as they needed. A picture taken once settles it without anyone's word
 * against anyone else's.
 */
export function setReferencePhoto(
  state: FamilyState,
  command: { taskId: TaskId; photoId: PhotoId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskReferencePhotoSet')) return fail('not-allowed')
  if (!state.tasks.has(command.taskId)) return fail('unknown-task')
  return done({
    ...base(ctx),
    type: 'TaskReferencePhotoSet',
    payload: { taskId: command.taskId, photoId: command.photoId },
  })
}

/**
 * Declares what kind of day it is. This is the "written exception" the study
 * asks for: a holiday, a school break or a sick day is decided once, in the
 * open, instead of being argued about when the chest will not open.
 */
export function declareDayType(
  _state: FamilyState,
  command: { day: DayId; kind: DayKind; note?: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'DayTypeDeclared')) return fail('not-allowed')
  return done({
    ...base(ctx),
    type: 'DayTypeDeclared',
    payload: {
      day: command.day,
      kind: command.kind,
      ...(command.note !== undefined ? { note: command.note } : {}),
    },
  })
}

/** A day that neither counts nor breaks anything. Not a punishment. */
/**
 * Un tramo del calendario de una vez.
 *
 * Es lo que hace usable la importación de un `.ics` y también lo que hace que
 * marcar unas vacaciones a mano sea una acción y no diecinueve. Se guarda el
 * tramo, no los días: es lo que el adulto decidió.
 */
export function declareDayRange(
  _state: FamilyState,
  command: { from: DayId; to: DayId; kind: DayKind; note?: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'DayRangeDeclared')) return fail('not-allowed')
  if (command.to < command.from) return fail('invalid', { field: 'to' })
  // Un curso son unos doscientos días. Mil es un fichero equivocado, y más vale
  // decirlo que desplegar medio siglo de calendario.
  if (diffDays(command.from, command.to) > 1000) return fail('invalid', { field: 'to' })

  return done({
    ...base(ctx),
    type: 'DayRangeDeclared',
    payload: {
      from: command.from,
      to: command.to,
      kind: command.kind,
      ...(command.note !== undefined && command.note.length > 0 ? { note: command.note } : {}),
    },
  })
}

export function pauseDay(
  state: FamilyState,
  command: { day: DayId; memberId: MemberId; reason: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'DayPaused')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')
  if (memberDayOf(state, command.day, command.memberId).paused) return fail('already-done')
  return done({
    ...base(ctx),
    type: 'DayPaused',
    payload: { day: command.day, memberId: command.memberId, reason: command.reason },
  })
}

// --- Doing a task ------------------------------------------------------------

export function completeStep(
  state: FamilyState,
  command: { taskId: TaskId; stepId: StepId },
  ctx: CommandContext,
): CommandResult {
  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')
  if (!task.steps.some((s) => s.id === command.stepId)) return fail('invalid', { field: 'stepId' })

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  if (today.status === 'validated') return fail('already-done')
  if (today.steps.has(command.stepId)) return done() // Tapping twice is not an error.

  return done({
    ...base(ctx),
    type: 'StepCompleted',
    payload: { taskId: task.id, memberId: task.memberId, stepId: command.stepId },
  })
}

/**
 * Undoes a tap. Children are children: they press things, sometimes each
 * other's things. Being able to take a tick back costs nothing, because a step
 * that has not been validated has not paid anything yet.
 */
export function uncompleteStep(
  state: FamilyState,
  command: { taskId: TaskId; stepId: StepId },
  ctx: CommandContext,
): CommandResult {
  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  // Once a parent has confirmed it, only they can reopen it.
  if (today.status === 'validated') return fail('already-done')
  if (!today.steps.has(command.stepId)) return done()
  if (!task.steps.some((s) => s.id === command.stepId)) return fail('invalid', { field: 'stepId' })

  return done({
    ...base(ctx),
    type: 'StepUncompleted',
    payload: { taskId: task.id, memberId: task.memberId, stepId: command.stepId },
  })
}

export function markTaskDone(
  state: FamilyState,
  command: { taskId: TaskId; commandId: CommandId; photoId?: PhotoId },
  ctx: CommandContext,
): CommandResult {
  if (state.seenCommands.has(command.commandId)) return done()

  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  if (today.status === 'validated') return fail('already-done')

  const missing = task.steps.filter((s) => !today.steps.has(s.id))
  if (missing.length > 0) {
    return fail('steps-missing', { count: missing.length, first: missing[0]?.text ?? '' })
  }
  if (task.requiresPhoto && command.photoId === undefined) return fail('photo-required')

  // From rung 3 the child validates their own work; below that a parent does.
  const selfValidated = task.rung >= 3

  const marked: DomainEvent = {
    ...base(ctx),
    type: 'TaskMarkedDone',
    payload: {
      taskId: task.id,
      memberId: task.memberId,
      commandId: command.commandId,
      ...(command.photoId !== undefined ? { photoId: command.photoId } : {}),
      selfValidated,
    },
  }

  return done(marked)
}

/** What a task pays at the rung it is on. */
export function rewardFor(
  rewards: RewardLadder,
  rung: Rung,
): { readonly coins: number; readonly xp: number } {
  return rewards[rung]
}

export function validateTask(
  state: FamilyState,
  command: { taskId: TaskId; commandId: CommandId },
  ctx: CommandContext,
): CommandResult {
  if (state.seenCommands.has(command.commandId)) return done()
  if (forbidden(ctx, 'TaskValidated')) return fail('not-allowed')

  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  if (today.status === 'validated') return fail('already-done')
  if (today.status !== 'marked') return fail('not-marked')

  const { coins, xp } = rewardFor(task.rewards, task.rung)

  return done({
    ...base(ctx),
    type: 'TaskValidated',
    payload: {
      taskId: task.id,
      memberId: task.memberId,
      commandId: command.commandId,
      rung: task.rung,
      coins,
      xp,
    },
  })
}

/**
 * Cuántas preguntas caben en una sesión. El mismo tope que el vocabulario, aquí
 * para poder rechazar en vez de reventar al escribir en el registro.
 */
const PREGUNTAS_MAXIMAS = 40

/**
 * Una sesión de repaso terminada.
 *
 * Guarda la sesión siempre —es el material con el que el juego decide en qué
 * insistir— y, además, da por hecha la misión del juego si hoy había una y no
 * estaba ya validada.
 *
 * Paga por **terminar la sesión**, no por acertar. El juego insiste a propósito
 * en lo que peor le sale, así que cobrar por el porcentaje sería cobrarle
 * justamente por la parte que todavía no sabe, y el camino corto para ganar más
 * sería evitar lo difícil. Lo que se gana es haber hecho el repaso; qué tal fue
 * se ve en Perspectivas.
 *
 * Emite los dos eventos que ya existen —marcar y validar— en vez de unos propios,
 * para que la misión del juego cuente en el día y en la racha como cualquier otra.
 */
export function finishPractice(
  state: FamilyState,
  command: {
    memberId: MemberId
    game: GameId
    items: readonly PracticeItem[]
    commandId: CommandId
  },
  ctx: CommandContext,
): CommandResult {
  if (state.seenCommands.has(command.commandId)) return done()
  if (forbidden(ctx, 'PracticeFinished')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')
  if (command.items.length === 0) return fail('invalid', { field: 'items' })
  if (command.items.length > PREGUNTAS_MAXIMAS) return fail('invalid', { field: 'items' })

  const finished: DomainEvent = {
    ...base(ctx),
    type: 'PracticeFinished',
    payload: {
      memberId: command.memberId,
      game: command.game,
      commandId: command.commandId,
      items: [...command.items],
    },
  }

  const mision = [...state.tasks.values()].find(
    (task) => task.memberId === command.memberId && task.game === command.game && !task.archived,
  )
  if (!mision) return done(finished)

  // Una sesión a medias se guarda —lo contestado es material igual de bueno para
  // aprender— pero no cobra. Si no, dejarlo en la segunda pregunta pagaría lo
  // mismo que hacerlo entero, y el camino corto sería empezar y salirse.
  if (command.items.length < PREGUNTAS_POR_SESION) return done(finished)

  const hoy = taskDayOf(state, ctx.day, command.memberId, mision.id)
  // Jugar otra vez no vuelve a pagar, y no es un error: es lo que evita que el
  // juego sea una mina. Quien llama lo distingue mirando si hay validación.
  if (hoy.status === 'validated') return done(finished)

  const { coins, xp } = rewardFor(mision.rewards, mision.rung)

  return done(
    finished,
    {
      ...base(ctx),
      type: 'TaskMarkedDone',
      payload: {
        taskId: mision.id,
        memberId: command.memberId,
        commandId: `${command.commandId}:hecha` as CommandId,
        selfValidated: true,
      },
    },
    {
      ...base(ctx),
      type: 'TaskValidated',
      payload: {
        taskId: mision.id,
        memberId: command.memberId,
        commandId: `${command.commandId}:vale` as CommandId,
        rung: mision.rung,
        coins,
        xp,
      },
    },
  )
}

export function returnTask(
  state: FamilyState,
  command: { taskId: TaskId; missingStepId?: StepId; note?: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TaskReturned')) return fail('not-allowed')

  const task = state.tasks.get(command.taskId)
  if (!task || task.archived) return fail('unknown-task')

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  if (today.status !== 'marked') return fail('not-marked')

  return done({
    ...base(ctx),
    type: 'TaskReturned',
    payload: {
      taskId: task.id,
      memberId: task.memberId,
      ...(command.missingStepId !== undefined ? { missingStepId: command.missingStepId } : {}),
      ...(command.note !== undefined ? { note: command.note } : {}),
    },
  })
}

/**
 * A parent undoing their own mistake: they confirmed the wrong task.
 *
 * Deliberately narrow. It needs a written reason, it only reaches today's work,
 * and it gives back at most what that one validation gave. It is not a way to
 * take points away as a consequence, and the app never offers it as one.
 */
export function revokeValidation(
  state: FamilyState,
  command: { taskId: TaskId; reason: string },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'ValidationRevoked')) return fail('not-allowed')
  if (command.reason.trim().length === 0) return fail('invalid', { field: 'reason' })

  const task = state.tasks.get(command.taskId)
  if (!task) return fail('unknown-task')

  const today = taskDayOf(state, ctx.day, task.memberId, task.id)
  if (today.status !== 'validated') return fail('not-marked')

  return done({
    ...base(ctx),
    type: 'ValidationRevoked',
    payload: {
      taskId: task.id,
      memberId: task.memberId,
      revokedEventId: `${task.id}:${ctx.day}` as never,
      reason: command.reason,
    },
  })
}

// --- The chest ---------------------------------------------------------------

/** Minutes the chest holds today, from the day's budget. */
export function chestMinutes(limits: ScreenLimits, budget: 'weekday' | 'weekend'): number {
  return limits.base[budget]
}

export function openChest(
  state: FamilyState,
  command: { memberId: MemberId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'ChestOpened')) return fail('not-allowed')

  const member = state.members.get(command.memberId)
  if (!member) return fail('unknown-member')

  const today = memberDayOf(state, ctx.day, command.memberId)
  if (today.chestOpened) return fail('chest-already-open')
  if (today.paused) return fail('no-screen-today')

  const budget = screenBudgetFor(dayOf(state, ctx.day).kind)
  if (budget === 'none') return fail('no-screen-today')

  // The chest follows the afternoon routine. This is a sequence, not a payment:
  // first what has to be done, then what is wanted.
  const afternoon = routinesOf(state, command.memberId, 'afternoon')
  if (afternoon.length === 0) return fail('chest-not-ready', { reason: 'no-afternoon-routine' })

  const pending = afternoon.filter(
    (task) => taskDayOf(state, ctx.day, command.memberId, task.id).status !== 'validated',
  )
  if (pending.length > 0) {
    return fail('chest-not-ready', { pending: pending.length, first: pending[0]?.name ?? '' })
  }

  return done({
    ...base(ctx),
    type: 'ChestOpened',
    payload: { memberId: command.memberId, minutes: chestMinutes(member.limits, budget) },
  })
}

// --- The shop ----------------------------------------------------------------

/**
 * Screen minutes already committed today: bought, whether or not a parent has
 * granted them yet. Counting only granted minutes would let a child buy ten
 * blocks and leave the parent to discover the cap afterwards.
 */
export function screenMinutesCommitted(state: FamilyState, day: DayId, memberId: MemberId): number {
  let minutes = 0
  const member = state.members.get(memberId)
  const block = member?.limits.blockMinutes ?? 5
  for (const purchase of state.purchases.values()) {
    if (purchase.memberId !== memberId || purchase.day !== day) continue
    // A change of mind must not keep eating the day's allowance, or undoing
    // would cost the child the thing they undid.
    if (purchase.status === 'cancelled') continue
    if (state.rewards.get(purchase.rewardId)?.kind !== 'screen') continue
    minutes += purchase.quantity * block
  }
  return minutes
}

function purchasesOfRewardInWindow(
  state: FamilyState,
  memberId: MemberId,
  rewardId: RewardId,
  from: DayId,
  to: DayId,
): number {
  let count = 0
  for (const purchase of state.purchases.values()) {
    if (purchase.memberId !== memberId || purchase.rewardId !== rewardId) continue
    if (purchase.day < from || purchase.day > to) continue
    // Same reason as the daily cap: what was undone was not used up.
    if (purchase.status === 'cancelled') continue
    count += purchase.quantity
  }
  return count
}

export function purchase(
  state: FamilyState,
  command: {
    purchaseId: PurchaseId
    memberId: MemberId
    rewardId: RewardId
    commandId: CommandId
    quantity?: number
  },
  ctx: CommandContext,
): CommandResult {
  if (state.seenCommands.has(command.commandId)) return done()
  if (forbidden(ctx, 'PurchaseMade')) return fail('not-allowed')

  const member = state.members.get(command.memberId)
  if (!member) return fail('unknown-member')

  const reward = state.rewards.get(command.rewardId)
  if (!reward?.active) return fail('unknown-reward')
  if (!reward.memberIds.includes(command.memberId)) return fail('reward-not-for-member')

  const quantity = command.quantity ?? 1
  if (quantity < 1) return fail('invalid', { field: 'quantity' })

  const cost = reward.price * quantity
  if (cost > member.coins) {
    return fail('not-enough-coins', { missing: cost - member.coins, price: cost })
  }

  if (reward.kind === 'screen') {
    const budget = screenBudgetFor(dayOf(state, ctx.day).kind)
    if (budget === 'none' || memberDayOf(state, ctx.day, command.memberId).paused) {
      return fail('no-screen-today')
    }
    const cap = member.limits.extraCap[budget]
    const asked = quantity * member.limits.blockMinutes
    const already = screenMinutesCommitted(state, ctx.day, command.memberId)
    // The cap exists so that the day's total stays where the parents put it,
    // no matter how much the child earns.
    if (already + asked > cap) {
      return fail('daily-cap-reached', { cap, already, asked })
    }
  }

  if (reward.limit) {
    const from = reward.limit.per === 'day' ? ctx.day : weekStart(ctx.day)
    const to = reward.limit.per === 'day' ? ctx.day : addDays(weekStart(ctx.day), 6)
    const used = purchasesOfRewardInWindow(state, command.memberId, reward.id, from, to)
    if (used + quantity > reward.limit.max) {
      return fail('reward-limit-reached', { max: reward.limit.max, used, per: reward.limit.per })
    }
  }

  return done({
    ...base(ctx),
    type: 'PurchaseMade',
    payload: {
      purchaseId: command.purchaseId,
      memberId: command.memberId,
      rewardId: reward.id,
      commandId: command.commandId,
      price: reward.price,
      quantity,
    },
  })
}

/**
 * A parent confirms they actually granted the minutes in Family Link. Without
 * this the app's balance and the tablet's reality drift apart within a week,
 * because buying minutes here concedes nothing by itself.
 */
/**
 * The child changed their mind.
 *
 * Only while the purchase is still waiting for an adult. After that the minutes
 * have been granted in Family Link and the coins are genuinely spent — undoing
 * it there would mean taking back something already given, which this app never
 * does to a child.
 *
 * The refund is exactly what that purchase cost, read from the purchase and not
 * from the reward: prices change at the weekly meeting, and giving back today's
 * price for something bought last week would be a small, silent lie in the
 * child's favour or against them.
 */
export function cancelPurchase(
  state: FamilyState,
  command: { purchaseId: PurchaseId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'PurchaseCancelled')) return fail('not-allowed')

  const purchase = state.purchases.get(command.purchaseId)
  if (!purchase) return fail('unknown-purchase')
  // A child may only undo their own change of mind. A parent may undo either.
  if (ctx.actor.kind === 'child' && ctx.actor.id !== purchase.memberId) return fail('not-allowed')
  if (purchase.status !== 'pending') return fail('already-given')

  return done({
    ...base(ctx),
    type: 'PurchaseCancelled',
    payload: {
      purchaseId: purchase.id,
      memberId: purchase.memberId,
      refund: purchase.price * purchase.quantity,
    },
  })
}

/**
 * Purchases that can no longer be what they were.
 *
 * Screen minutes belong to a day. If nobody granted them and that day is over,
 * there is nothing left to hand over: the minutes were Monday's and it is now
 * Tuesday. Leaving the purchase pending would mean a child paid for something
 * that cannot arrive, which is the one thing this app must never do.
 *
 * Only screen time expires. "Elegir la película del viernes" bought on Tuesday
 * is not stale on Wednesday; it is waiting for Friday, and a parent resolves it
 * when it comes.
 */
export function stalePurchases(state: FamilyState, today: DayId): readonly PurchaseId[] {
  const stale: PurchaseId[] = []
  for (const purchase of state.purchases.values()) {
    if (purchase.status !== 'pending') continue
    if (purchase.day >= today) continue
    if (state.rewards.get(purchase.rewardId)?.kind !== 'screen') continue
    stale.push(purchase.id)
  }
  return stale
}

/**
 * Gives back a purchase the day left behind. Same event as a change of mind,
 * because from the child's side it is the same thing: the coins come back and
 * nothing arrived.
 */
export function expirePurchase(
  state: FamilyState,
  command: { purchaseId: PurchaseId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'PurchaseCancelled')) return fail('not-allowed')

  const purchase = state.purchases.get(command.purchaseId)
  if (!purchase) return fail('unknown-purchase')
  if (purchase.status !== 'pending') return fail('already-given')

  return done({
    ...base(ctx),
    type: 'PurchaseCancelled',
    payload: {
      purchaseId: purchase.id,
      memberId: purchase.memberId,
      refund: purchase.price * purchase.quantity,
    },
  })
}

export function grantPurchase(
  state: FamilyState,
  command: { purchaseId: PurchaseId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'PurchaseGranted')) return fail('not-allowed')

  const target = state.purchases.get(command.purchaseId)
  if (!target) return fail('unknown-purchase')
  if (target.status !== 'pending') return fail('already-done')

  const reward = state.rewards.get(target.rewardId)
  const member = state.members.get(target.memberId)
  const minutes =
    reward?.kind === 'screen' ? target.quantity * (member?.limits.blockMinutes ?? 5) : 0

  return done({
    ...base(ctx),
    type: 'PurchaseGranted',
    payload: { purchaseId: target.id, minutes },
  })
}

export function deliverPurchase(
  state: FamilyState,
  command: { purchaseId: PurchaseId },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'PurchaseDelivered')) return fail('not-allowed')
  const target = state.purchases.get(command.purchaseId)
  if (!target) return fail('unknown-purchase')
  if (target.status === 'delivered') return fail('already-done')
  return done({ ...base(ctx), type: 'PurchaseDelivered', payload: { purchaseId: target.id } })
}

// --- The timer ---------------------------------------------------------------

/**
 * Starts the visible countdown. It is stored as the instant it ends, never as a
 * number of seconds ticking down: a web app in the background is suspended, so
 * anything that counts rather than compares comes back wrong.
 */
export function startTimer(
  state: FamilyState,
  command: { memberId: MemberId; minutes: number; source: 'chest' | 'purchase' },
  ctx: CommandContext,
): CommandResult {
  if (forbidden(ctx, 'TimerStarted')) return fail('not-allowed')
  if (!state.members.has(command.memberId)) return fail('unknown-member')
  if (command.minutes < 1) return fail('invalid', { field: 'minutes' })

  const endsAt = new Date(ctx.now.getTime() + command.minutes * 60_000).toISOString()

  return done({
    ...base(ctx),
    type: 'TimerStarted',
    payload: {
      memberId: command.memberId,
      minutes: command.minutes,
      endsAt,
      source: command.source,
    },
  })
}
